import React, { useState, useEffect } from "react";
import "../../index.css";
import cmsApi from "../../services/cmsApi";
import { getDocumentUrl } from "../../utils/getDocumentUrl";

const QuickDownloads = () => {
  const [componentData, setComponentData] = useState({
    title: "",
    subtitle: "",
    downloads: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    // Only fetch on client side to avoid hydration issues
    if (typeof window === 'undefined') return;
    
    const fetchQuickDownloads = async () => {
      try {
        setLoading(true);
        setError("");
        // Try both type names for compatibility
        let result = await cmsApi.getHomepageComponentByType('quick-downloads');
        if (!result.success || !result.components || result.components.length === 0) {
          result = await cmsApi.getHomepageComponentByType('downloads');
        }
        
        if (result.success && result.components && result.components.length > 0) {
          const component = result.components[0];
          
          // Parse downloads JSON
          let downloads = [];
          if (component.downloads) {
            try {
              const parsedDownloads = JSON.parse(component.downloads);
              downloads = parsedDownloads.map((item) => {
                // Determine link and action type based on item data
                let link = '#';
                let actionType = 'document';
                
                if (item.actionType === 'external' && item.externalUrl) {
                  // External link
                  link = item.externalUrl;
                  actionType = 'external';
                } else if (item.documentUrl) {
                  // Document upload - resolve URL properly
                  const resolvedUrl = getDocumentUrl(item.documentUrl);
                  link = resolvedUrl;
                  actionType = 'document';
                }
                
                return {
                  title: item.title,
                  buttonText: item.buttonText,
                  link: link,
                  actionType: actionType, // Store actionType for proper handling
                };
              });
            } catch (e) {
              console.error('Error parsing downloads:', e);
            }
          }
          
          setComponentData({
            title: component.title || "",
            subtitle: component.subtitle || "",
            downloads: downloads,
          });
        } else {
          setComponentData({ title: "", subtitle: "", downloads: [] });
          setError("Downloads are unavailable right now.");
        }
      } catch (error) {
        setComponentData({ title: "", subtitle: "", downloads: [] });
        setError("Unable to load quick downloads.");
      } finally {
        setLoading(false);
      }
    };

    fetchQuickDownloads();
  }, []);

  if (loading) {
    return (
      <section className="bg-gray-100 pb-10 pt-8">
        <div className="max-w-7xl mx-auto text-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-custom-pink border-t-transparent mx-auto"></div>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-gray-100 pb-10 pt-8 min-[1024px]:max-[1535px]:!pb-8 min-[1024px]:max-[1535px]:!pt-6">
      {/* Header with blinking effect */}
      <div className="max-w-7xl mx-auto text-center">
        <h2 className="text-3xl sm:text-4xl font-bold text-custom-pink mb-4 blink min-[1024px]:max-[1535px]:!text-2xl min-[1024px]:max-[1535px]:!mb-3">
          {componentData.title || "QUICK APPLICATION & DOWNLOADS"}
        </h2>
        <span className="inline-block border-solid border-b-4 border-gold mb-4 w-20 sm:w-28 h-2 blink min-[1024px]:max-[1535px]:!mb-3 min-[1024px]:max-[1535px]:!w-24 min-[1024px]:max-[1535px]:!border-b-2"></span>

        <p className="text-lg sm:text-xl text-gray-600 mb-8 min-[1024px]:max-[1535px]:!text-base min-[1024px]:max-[1535px]:!mb-6">
          {componentData.subtitle || "Access important documents from the CMS."}
        </p>
        {error && <p className="text-sm text-red-500 mb-6">{error}</p>}
      </div>

      {/* Downloads */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 justify-center min-[1024px]:max-[1535px]:!gap-4">
        {componentData.downloads.map((item, index) => {
          // Use actionType if available, otherwise infer from link pattern
          // Note: actionType is now stored in the item from the mapping above
          const isExternal = item.actionType === 'external';
          const isDownload = item.actionType === 'document';
          
          // Handle click for downloads to ensure proper download behavior
          const handleClick = (e) => {
            if (!item.link || item.link === '#') {
              // Invalid link
              e.preventDefault();
              console.warn('Invalid download link:', item.link);
              return;
            }
            
            if (isDownload) {
              // For document downloads, let the browser handle it with download attribute
              // The download attribute will force download instead of navigation
              return; // Let default behavior happen
            } else if (isExternal) {
              // External links open in new tab (handled by target="_blank")
              return; // Let default behavior happen
            }
          };
          
          return (
          <div
            key={index}
            className="bg-white p-6 rounded-lg shadow-lg hover:shadow-2xl transition-shadow duration-300 min-[1024px]:max-[1535px]:!p-4 min-[1024px]:max-[1535px]:!rounded-md"
          >
            <div className="flex flex-col items-start">
              <h3 className="text-xl font-semibold mb-2 text-gray-800 min-[1024px]:max-[1535px]:!text-base min-[1024px]:max-[1535px]:!mb-1.5">
                {item.title}
              </h3>
                <a
                  href={item.link}
                  target={isExternal || isDownload ? "_blank" : undefined}
                  download={isDownload && item.link !== '#' ? true : undefined}
                  onClick={handleClick}
                  className="inline-block bg-custom-pink text-white py-2 px-4 rounded-lg hover:bg-gold transition-colors duration-300 min-[1024px]:max-[1535px]:!py-1.5 min-[1024px]:max-[1535px]:!px-3 min-[1024px]:max-[1535px]:!text-sm"
                  rel={isExternal || isDownload ? "noreferrer noopener" : undefined}
                >
                  {item.buttonText}
                </a>
            </div>
          </div>
          );
        })}
      </div>
      {!loading && componentData.downloads.length === 0 && (
        <div className="max-w-7xl mx-auto px-4 text-center text-gray-500 mt-6">
          No downloads available at the moment.
        </div>
      )}
    </section>
  );
};

export default QuickDownloads;
